import React from 'react';
import type { MockOrder } from '../types';

export interface PhotosTabProps {
  order: MockOrder;
}

interface PhotoSlot {
  id: string;
  label: string;
  hint: string;
  /** Tailwind gradient pair used as the placeholder thumbnail. */
  gradient: string;
  /** When `true`, the slot is filled (mock); otherwise it's an empty upload tile. */
  filled: boolean;
}

/**
 * Build the slot grid based on order status. A delivered order has
 * pickup + drop-off photos + signature; an in-transit order has
 * pickup only; pre-pickup orders show empty upload tiles.
 */
const buildSlots = (order: MockOrder): PhotoSlot[] => {
  const hasPickup =
    order.status === 'in_transit' || order.status === 'delivered';
  const hasDropoff = order.status === 'delivered';

  return [
    {
      id: 'pickup-1',
      label: 'Pickup POD',
      hint: order.pickup.city,
      gradient: 'from-amber-200 via-orange-200 to-rose-200',
      filled: hasPickup,
    },
    {
      id: 'pickup-2',
      label: 'Pickup detail',
      hint: 'Cargo seal',
      gradient: 'from-emerald-200 via-teal-200 to-cyan-200',
      filled: hasPickup,
    },
    {
      id: 'dropoff-1',
      label: 'Drop-off POD',
      hint: order.dropoff.city,
      gradient: 'from-indigo-200 via-violet-200 to-fuchsia-200',
      filled: hasDropoff,
    },
    {
      id: 'signature',
      label: 'Receiver signature',
      hint: 'On waybill',
      gradient: 'from-slate-200 via-gray-200 to-zinc-200',
      filled: hasDropoff,
    },
  ];
};

/**
 * Photo grid for the expanded row. Filled slots show a gradient
 * placeholder thumbnail (we don't have real images in the mock);
 * empty slots show a dashed upload affordance with a "Pending"
 * hint.
 */
export const PhotosTab: React.FC<PhotosTabProps> = ({ order }) => {
  const slots = buildSlots(order);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[13px] text-gray-600">
          Proof-of-delivery and supporting photos
        </p>
        <button
          type="button"
          onClick={() => console.log('[orders-preview] upload photo')}
          className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[12px] font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50">
          Upload
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {slots.map(slot => (
          <button
            key={slot.id}
            type="button"
            onClick={() =>
              console.log(
                `[orders-preview] open photo ${slot.id} (filled=${slot.filled})`,
              )
            }
            className={`group flex flex-col overflow-hidden rounded-lg border ${
              slot.filled
                ? 'border-gray-200 bg-white'
                : 'border-dashed border-gray-300 bg-gray-50/50'
            } text-start hover:border-gray-300 transition-colors`}>
            <div
              className={`relative flex h-28 w-full items-center justify-center bg-gradient-to-br ${slot.gradient}`}>
              {!slot.filled && (
                <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200">
                  Pending
                </span>
              )}
              {slot.filled && (
                <span className="absolute bottom-1.5 end-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  Photo
                </span>
              )}
            </div>
            <div className="px-2.5 py-2">
              <p className="truncate text-[12px] font-medium text-gray-900">
                {slot.label}
              </p>
              <p className="truncate text-[11px] text-gray-500">{slot.hint}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
